import * as React from 'react';
import { useState } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, Grid, Box, Chip, Tabs, Tab, TextField, FormControlLabel, Checkbox, MenuItem, Menu, Select, Stack, CircularProgress } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { 
  Loading, 
  useGetOne, 
  useRecordContext, 
  useTranslate, 
  useNotify, 
  Identifier,
  ReferenceField,
  RecordContextProvider,
  useGetMany,
  useGetList,
  useDataProvider,
  useGetIdentity
} from 'react-admin';
import { Contract, CustomerEventType } from '../types';
import { supabase } from '../lib/supabaseClient';
import { isPreplanOnly } from '../funerals/preneedMode';
import { getRefreshToken, getToken } from '../authentication/authProvider';

interface ClientInfo {
  id: Identifier;
  first_name?: string;
  last_name?: string;
  email?: string;
  address?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  phone_number?: string;
  birthdate?: string;
}

interface FuneralData {
  id: Identifier;
  uuid?: string;
  paying_client_id?: Identifier;
  authorized_client_id?: Identifier;
  deceased_client_id?: Identifier;
  date_time_of_death?: string;
  place_of_death?: string;
  region?: 'ontario' | 'quebec';
  crematorium_partner_id?: Identifier;
  has_visitation?: boolean;
}

interface CrematoriumSuggestion {
  eligible: boolean;
  suggestedPartnerId: string | null;
  suggestedPartnerName: string | null;
  defaultPartnerId: string | null;
  defaultPartnerName: string | null;
  monthlyTarget: number;
  expectedToDate: number;
  countSoFar: number;
  reason: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface SubmissionData {
  type: string;
  slug: string;
  submissionId?: number;
}

// One entry of the preview's Download menu: which submission to pull the PDF from and
// which slice of it ('contract' / 'agreements' / 'all' documents merged).
interface DownloadOption {
  label: string;
  submissionId: number;
  part: 'all' | 'contract' | 'agreements';
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`contract-tabpanel-${index}`}
      aria-labelledby={`contract-tab-${index}`}
      {...other}
      style={{ height: 'calc(100% - 48px)', overflow: 'auto' }}
    >
      {value === index && <Box sx={{ p: 0, height: '100%' }}>{children}</Box>}
    </div>
  );
}

const ClientInfo = ({ clientId, label }: { clientId?: Identifier, label: string }) => {
  const { data: client, isLoading } = useGetOne<ClientInfo>(
    'clients',
    { id: clientId || 0 },
    { enabled: !!clientId }
  );

  if (isLoading) return <Loading />;

  if (!client) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">{label}</Typography>
        <Chip label="Missing information" color="error" size="small" />
      </Box>
    );
  }

  const missingFields = [];
  if (!client.email) missingFields.push('Email');
  if (!client.address) missingFields.push('Address');
  if (!client.phone_number) missingFields.push('Phone');

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1">{label}</Typography>
      <Typography variant="body2">
        <strong>Name:</strong> {client.first_name} {client.last_name || ''}
      </Typography>
      <Typography variant="body2">
        <strong>Email:</strong> {client.email || 'Not provided'}
        {!client.email && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Address:</strong> {client.address ? 
          `${client.address}, ${client.city || ''} ${client.province || ''} ${client.postal_code || ''}` : 
          'Not provided'}
        {!client.address && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Phone:</strong> {client.phone_number || 'Not provided'}
        {!client.phone_number && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
    </Box>
  );
};

const DeceasedInfo = ({ clientId, funeralData }: { clientId?: Identifier, funeralData?: FuneralData }) => {
  const { data: deceased, isLoading } = useGetOne<ClientInfo>(
    'clients',
    { id: clientId || 0 },
    { enabled: !!clientId }
  );

  if (isLoading) return <Loading />;

  if (!deceased) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1">Deceased Information</Typography>
        <Chip label="Missing information" color="error" size="small" />
      </Box>
    );
  }

  const missingFields = [];
  if (!deceased.birthdate) missingFields.push('Date of Birth');
  if (!funeralData?.date_time_of_death) missingFields.push('Date of Death');
  if (!funeralData?.place_of_death) missingFields.push('Place of Death');

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle1">Deceased Information</Typography>
      <Typography variant="body2">
        <strong>Name:</strong> {deceased.first_name} {deceased.last_name || ''}
      </Typography>
      <Typography variant="body2">
        <strong>Address:</strong> {deceased.address ? 
          `${deceased.address}, ${deceased.city || ''} ${deceased.province || ''} ${deceased.postal_code || ''}` : 
          'Not provided'}
        {!deceased.address && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Date of Birth:</strong> {deceased.birthdate || 'Not provided'} 
        {!deceased.birthdate && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Date of Death:</strong> {funeralData?.date_time_of_death || 'Not provided'}
        {!funeralData?.date_time_of_death && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Place of Death:</strong> {funeralData?.place_of_death || 'Not provided'}
        {!funeralData?.place_of_death && <Chip label="Missing" color="error" size="small" sx={{ ml: 1 }} />}
      </Typography>
      <Typography variant="body2">
        <strong>Phone:</strong> {deceased.phone_number || 'Not provided'}
      </Typography>
    </Box>
  );
};

export const SendContractButton = () => {
  const record = useRecordContext<Contract>();
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [includeContract, setIncludeContract] = useState(true);
  const [includeAgreements, setIncludeAgreements] = useState(true);
  // T-162: email the signed TruStage enrollment form to TruStage when the family signs.
  const [sendToTrustage, setSendToTrustage] = useState(true);
  const [trustagePreviewOpen, setTrustagePreviewOpen] = useState(false);
  const [agreementType, setAgreementType] = useState<string>('cleo');
  const [agreementRecipientId, setAgreementRecipientId] = useState<string>('');
  const [agreementRecipientName, setAgreementRecipientName] = useState('');
  const [agreementRecipientEmail, setAgreementRecipientEmail] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);
  // Which agreement the preview renders. Null until the case data has loaded and the
  // partner-derived agreement is known — the preview request must never fire with a
  // placeholder default, since the backend pre-fills fields specific to the requested
  // agreement's template. Staff can then toggle it to eyeball another version.
  const [previewAgreementType, setPreviewAgreementType] = useState<string | null>(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = useState<null | HTMLElement>(null);
  const [downloading, setDownloading] = useState(false);
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { identity } = useGetIdentity();

  // Load DocuSeal React component dynamically
  const [DocusealForm, setDocusealForm] = useState<any>(null);
  React.useEffect(() => {
    if (previewOpen && !DocusealForm) {
      import('@docuseal/react').then(module => {
        setDocusealForm(() => module.DocusealForm);
      }).catch(err => {
        console.error('Failed to load DocuSeal component:', err);
        notify('Failed to load document viewer', { type: 'error' });
      });
    }
  }, [previewOpen, DocusealForm, notify]);

  const { data: contract, isLoading: isContractLoading } = useGetOne(
    'contracts',
    { id: record?.id },
    { 
      enabled: !!record?.id && (open || previewOpen),
      refetchOnWindowFocus: false
    }
  );

  const { data: funeral, isLoading: isFuneralLoading, refetch: refetchFuneral } = useGetOne<FuneralData>(
    'funerals',
    { id: contract?.funeral_id },
    {
      enabled: !!contract?.funeral_id && (open || previewOpen),
      refetchOnWindowFocus: false
    }
  );

  // The crematorium partner is the source of truth for which cremation agreement to send.
  const { data: crematoriumPartner, isError: isPartnerError, refetch: refetchPartner } = useGetOne(
    'partners',
    { id: funeral?.crematorium_partner_id },
    {
      enabled: !!funeral?.crematorium_partner_id && (open || previewOpen),
      refetchOnWindowFocus: false,
    }
  );

  const [suggestion, setSuggestion] = useState<CrematoriumSuggestion | null>(null);
  const [suggestionLoading, setSuggestionLoading] = useState(false);
  const [applyingSuggestion, setApplyingSuggestion] = useState(false);

  const clientIds = React.useMemo(() => {
    const ids = new Set<string>();
    if (funeral?.paying_client_id) ids.add(String(funeral.paying_client_id));
    if (funeral?.authorized_client_id) ids.add(String(funeral.authorized_client_id));
    if (funeral?.deceased_client_id) ids.add(String(funeral.deceased_client_id));
    return Array.from(ids);
  }, [funeral]);

  const { data: clients } = useGetMany(
    'clients',
    { ids: clientIds },
    { enabled: clientIds.length > 0 }
  );

  const isSameRecipient = React.useMemo(() => {
    if (!funeral?.paying_client_id || !funeral?.authorized_client_id) return true;
    return String(funeral.paying_client_id) === String(funeral.authorized_client_id);
  }, [funeral?.paying_client_id, funeral?.authorized_client_id]);

  // A contract is "subsequent" when it isn't the first one created for the funeral
  // (an amendment or follow-up). For those we default the agreements off — they were
  // already handled on the original contract — but the user can re-enable them below.
  const showTrustageBox = !!contract?.is_preneed && !isPreplanOnly(funeral as any);
  const { data: funeralContractsList } = useGetList(
    'contracts',
    {
      filter: { funeral_id: contract?.funeral_id },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    },
    { enabled: !!contract?.funeral_id && (open || previewOpen) }
  );

  const isSubsequentContract = React.useMemo(() => {
    const first = funeralContractsList?.[0];
    return !!first && !!contract?.id && String(first.id) !== String(contract.id);
  }, [funeralContractsList, contract?.id]);

  const payingClientInfo = React.useMemo(() =>
    (clients || []).find((c: any) => String(c.id) === String(funeral?.paying_client_id)),
    [clients, funeral?.paying_client_id]
  );

  const authorizedClientInfo = React.useMemo(() =>
    (clients || []).find((c: any) => String(c.id) === String(funeral?.authorized_client_id)),
    [clients, funeral?.authorized_client_id]
  );

  // The agreement the send flow will resolve for this case: crematorium partner (source of
  // truth), falling back to region. Null while the funeral — and the partner, when one is
  // set — is still loading, so consumers can tell "not derived yet" apart from a real value.
  const derivedAgreementType = React.useMemo(() => {
    if (!funeral) return null;
    if (funeral.crematorium_partner_id && !crematoriumPartner && !isPartnerError) return null;
    if (crematoriumPartner?.agreement_type) return crematoriumPartner.agreement_type as string;
    if (funeral.region === 'quebec') return 'rideau';
    if (funeral.region === 'ontario') return 'southwest';
    return 'cleo';
  }, [funeral, crematoriumPartner, isPartnerError]);

  React.useEffect(() => {
    if (derivedAgreementType) setAgreementType(derivedAgreementType);
  }, [derivedAgreementType]);

  // Fetch the crematorium routing suggestion for QC at-need cases. Whether visitation rules the
  // case out is the backend's call (it depends on which crematoria accept visitation), so it is
  // not pre-filtered here; an ineligible answer simply renders nothing.
  React.useEffect(() => {
    const eligible =
      open &&
      !!funeral?.id &&
      funeral?.region === 'quebec' &&
      !contract?.is_preneed;
    if (!eligible) {
      setSuggestion(null);
      return;
    }
    let cancelled = false;
    setSuggestionLoading(true);
    void (async () => {
      try {
        const token = await getToken();
        const res = await fetch(
          `${process.env.REACT_APP_PAPERMAN_API_URL}contract/crematorium-suggestion/${funeral.id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error(`status ${res.status}`);
        const data = (await res.json()) as CrematoriumSuggestion;
        if (!cancelled) setSuggestion(data);
      } catch (err) {
        console.error('Failed to load crematorium suggestion:', err);
        if (!cancelled) setSuggestion(null);
      } finally {
        if (!cancelled) setSuggestionLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, funeral?.id, funeral?.region, contract?.is_preneed]);

  const applySuggestedPartner = async () => {
    if (!funeral?.id || !suggestion?.suggestedPartnerId) return;
    setApplyingSuggestion(true);
    try {
      await dataProvider.update('funerals', {
        id: funeral.id,
        data: { crematorium_partner_id: suggestion.suggestedPartnerId },
        previousData: funeral,
      });
      notify(`Crematorium set to ${suggestion.suggestedPartnerName}`, { type: 'success' });
      await refetchFuneral();
      await refetchPartner();
    } catch (err) {
      console.error('Failed to set crematorium partner:', err);
      notify('Failed to set crematorium', { type: 'error' });
    } finally {
      setApplyingSuggestion(false);
    }
  };

  React.useEffect(() => {
    if (contract?.is_preneed) {
      setIncludeAgreements(false);
    }
  }, [contract?.is_preneed]);

  // Default the agreements checkbox OFF for subsequent (non-first) at-need contracts.
  // Applied once per dialog open, after the contract list has loaded, so it sets the
  // default without clobbering a user's subsequent toggle. Preneed is handled above.
  const subsequentAgreementsDefaultedRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      subsequentAgreementsDefaultedRef.current = false;
      return;
    }
    if (contract?.is_preneed) return;
    if (!funeralContractsList) return; // wait for the list to load
    if (subsequentAgreementsDefaultedRef.current) return;
    subsequentAgreementsDefaultedRef.current = true;
    if (isSubsequentContract) {
      setIncludeAgreements(false);
    }
  }, [open, contract?.is_preneed, funeralContractsList, isSubsequentContract]);

  React.useEffect(() => {
    if (!contract?.is_preneed || !includeAgreements) {
      return;
    }

    if (!agreementRecipientId) {
      if (funeral?.authorized_client_id) {
        setAgreementRecipientId(String(funeral.authorized_client_id));
      } else if (clients && clients.length > 0) {
        setAgreementRecipientId(String(clients[0].id));
      }
      return;
    }

    if (agreementRecipientId === 'other') {
      setAgreementRecipientName('');
      setAgreementRecipientEmail('');
      return;
    }

    const selectedClient = (clients || []).find(
      (client: any) => String(client.id) === agreementRecipientId
    );
    if (selectedClient) {
      setAgreementRecipientName(selectedClient.name || '');
      setAgreementRecipientEmail(selectedClient.email || '');
    }
  }, [agreementRecipientId, clients, funeral?.authorized_client_id, contract?.is_preneed, includeAgreements]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handlePreviewOpen = () => {
    setPreviewOpen(true);
  };

  const handlePreviewClose = () => {
    setPreviewOpen(false);
    setCurrentTab(0);
    setDownloadMenuAnchor(null);
    setPreviewData(null); // Clear preview data when closing
    setPreviewAgreementType(null); // Re-seed from the derived agreement on next open
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  // Function to create a customer event
  const createCustomerEvent = async (eventData: any) => {
    if (!funeral?.id) return null;
    
    try {
      const event = {
        funeral_id: Number(funeral.id),
        timestamp: new Date().toISOString(),
        user_id: identity?.id ? Number(identity.id) : undefined,
        user_name: identity?.fullName || 'System',
        ...eventData
      };
      
      const response = await dataProvider.create('customer_events', { data: event });
      return response.data;
    } catch (error) {
      console.error('Error creating customer event:', error);
      return null;
    }
  };

  const handleSendContract = async () => {
    setLoading(true);
    try {
      if (!includeContract && !includeAgreements) {
        notify('Please select at least one document to send', { type: 'warning' });
        setLoading(false);
        return;
      }

      if (contract?.is_preneed && includeAgreements && agreementRecipientId === 'other') {
        if (!agreementRecipientName || !agreementRecipientEmail) {
          notify('Agreement recipient name and email are required', { type: 'warning' });
          setLoading(false);
          return;
        }
      }

      // T-162: remember the TruStage choice on the contract before sending, so
      // the signing webhook (which can fire within seconds) reads it.
      if (showTrustageBox && includeContract) {
        const { error: forwardError } = await supabase
          .from('contracts')
          .update({ trustage_forward: sendToTrustage })
          .eq('id', contract!.id);
        if (forwardError) throw new Error(`Could not save the TruStage choice: ${forwardError.message}`);
      }

      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_PAPERMAN_API_URL}contract/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),

        },
        body: JSON.stringify({
          uuid: funeral?.uuid,
          contractId: contract?.id,
          // Backward-compatible preneed agreements-only flag
          sendAgreements: contract?.is_preneed && includeAgreements && !includeContract,
          agreementRecipientClientId:
            contract?.is_preneed && includeAgreements && agreementRecipientId && agreementRecipientId !== 'other'
              ? Number(agreementRecipientId)
              : undefined,
          agreementRecipientName: contract?.is_preneed && includeAgreements ? agreementRecipientName : undefined,
          agreementRecipientEmail: contract?.is_preneed && includeAgreements ? agreementRecipientEmail : undefined,
          // New fields for agreement type and document selection
          includeContract,
          includeAgreements,
          agreementType,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to send contract');
      }

      const result = await response.json();
      
      // Create customer events for contract sent
      const payingClient = funeral?.paying_client_id as any;
      const authorizedClient = funeral?.authorized_client_id as any;
      
      // Fetch client details for event messages
      const { data: payingClientData } = await dataProvider.getOne('clients', { id: payingClient });
      const { data: authorizedClientData } = await dataProvider.getOne('clients', { id: authorizedClient });
      
      // Check if it's same client or different clients
      const isSameClient = payingClientData?.email === authorizedClientData?.email;
      
      // Check if it's preneed
      const isPreneed = contract?.is_preneed;
      
      const agreementLabel = agreementType === 'rideau' ? 'Rideau' : agreementType === 'southwest' ? 'Southwest' : 'Cleo';

      if (isPreneed) {
        if (includeAgreements) {
          const recipientName = agreementRecipientName || `${authorizedClientData?.first_name || ''} ${authorizedClientData?.last_name || ''}`.trim();
          const recipientEmail = agreementRecipientEmail || authorizedClientData?.email;
          const recipientId = agreementRecipientId && agreementRecipientId !== 'other'
            ? Number(agreementRecipientId) : Number(authorizedClient);
          await createCustomerEvent({
            type: CustomerEventType.CONTRACT_SENT,
            recipient_type: 'customer',
            recipient_id: recipientId,
            recipient_name: recipientName,
            contact_method: 'email',
            contact_detail: recipientEmail,
            message: `Sent ${agreementLabel} Cremation & Photograph Agreements to ${recipientName} at ${recipientEmail}`
          });
        }
        if (includeContract) {
          await createCustomerEvent({
            type: CustomerEventType.CONTRACT_SENT,
            recipient_type: 'customer',
            recipient_id: Number(payingClient),
            recipient_name: `${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''}`.trim(),
            contact_method: 'email',
            contact_detail: payingClientData?.email,
            message: `Sent preneed contract to ${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''} at ${payingClientData?.email}`
          });
        }
      } else if (isSameClient) {
        // At-need same client: build message based on what's included
        const docs = [];
        if (includeContract) docs.push('Cleo Contract');
        if (includeAgreements) docs.push(`${agreementLabel} Cremation Agreement`, 'Photograph Agreement');
        if (docs.length > 0) {
          await createCustomerEvent({
            type: CustomerEventType.CONTRACT_SENT,
            recipient_type: 'customer',
            recipient_id: Number(payingClient),
            recipient_name: `${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''}`.trim(),
            contact_method: 'email',
            contact_detail: payingClientData?.email,
            message: `Sent ${docs.join(', ')} to ${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''} at ${payingClientData?.email}`
          });
        }
      } else {
        // At-need different clients: Split contracts
        if (includeContract) {
          await createCustomerEvent({
            type: CustomerEventType.CONTRACT_SENT,
            recipient_type: 'customer',
            recipient_id: Number(payingClient),
            recipient_name: `${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''}`.trim(),
            contact_method: 'email',
            contact_detail: payingClientData?.email,
            message: `Sent Cleo Contract to ${payingClientData?.first_name || ''} ${payingClientData?.last_name || ''} at ${payingClientData?.email}`
          });
        }

        if (includeAgreements) {
          await createCustomerEvent({
            type: CustomerEventType.CONTRACT_SENT,
            recipient_type: 'customer',
            recipient_id: Number(authorizedClient),
            recipient_name: `${authorizedClientData?.first_name || ''} ${authorizedClientData?.last_name || ''}`.trim(),
            contact_method: 'email',
            contact_detail: authorizedClientData?.email,
            message: `Sent ${agreementLabel} Cremation & Photograph Agreements to ${authorizedClientData?.first_name || ''} ${authorizedClientData?.last_name || ''} at ${authorizedClientData?.email}`
          });
        }
      }
      
      const sentParts = [];
      if (includeContract) sentParts.push('Contract');
      if (includeAgreements) sentParts.push('Agreements');
      notify(`${sentParts.join(' & ')} sent successfully`, { type: 'success' });
      setOpen(false);
    } catch (error: unknown) {
      console.error('Error sending contract:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send contract';
      notify(errorMessage, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };


  // Agreement versions offered in the preview toggle, by region. The first is the
  // regional default; the list lets staff eyeball the alternate before sending.
  const previewAgreementChoices = React.useMemo(() => {
    const region = funeral?.region?.toLowerCase();
    if (region === 'quebec') {
      return [
        { id: 'cleo', name: 'Cleo (Lakeshore Cardinal)' },
        { id: 'rideau', name: 'Rideau (RETO)' },
      ];
    }
    if (region === 'ontario') {
      return [
        { id: 'southwest', name: 'Southwest' },
        { id: 'cleo', name: 'Cleo' },
      ];
    }
    return [{ id: 'cleo', name: 'Cleo' }];
  }, [funeral?.region]);

  const handlePreviewContract = async () => {
    setPreviewLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${process.env.REACT_APP_PAPERMAN_API_URL}contract/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Refresh-Token': await getRefreshToken(),
        },
        body: JSON.stringify({
          uuid: funeral?.uuid,
          contractId: contract?.id,
          // Preview the agreement that will actually be sent (partner-derived), unless
          // staff have toggled the preview to a different version.
          agreementType: previewAgreementType,
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to preview contract');
      }

      const result = await response.json();
      setPreviewData(result.data);
    } catch (error: unknown) {
      console.error('Error previewing contract:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to preview contract';
      notify(errorMessage, { type: 'error' });
      handlePreviewClose();
    } finally {
      setPreviewLoading(false);
    }
  };

  // When the preview opens, seed the preview's agreement from the partner-derived
  // agreement so the preview matches what will be sent. Seeding from the derived value
  // (not the async agreementType state) guarantees the first preview request can't fire
  // with a stale default while the case data is still resolving.
  React.useEffect(() => {
    if (previewOpen && derivedAgreementType) setPreviewAgreementType(derivedAgreementType);
  }, [previewOpen, derivedAgreementType]);

  // (Re)fetch the preview when it opens or when the previewed agreement changes — but
  // only once the previewed agreement has been seeded.
  React.useEffect(() => {
    if (previewOpen && funeral?.uuid && previewAgreementType) {
      setPreviewData(null);
      void handlePreviewContract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewOpen, funeral?.uuid, previewAgreementType]);

  // What the Download menu offers, mirroring how the documents are actually laid out in
  // DocuSeal: at-need same-client cases merge everything into one submission (Cleo
  // contract first, agreements after), different-client cases have one submission each,
  // and preneed has no agreements at all.
  const downloadOptions = React.useMemo<DownloadOption[]>(() => {
    const submissions: SubmissionData[] = previewData?.submissions || [];
    const findId = (type: string) =>
      submissions.find((sub) => sub.type === type && sub.submissionId)?.submissionId;

    const allContractsId = findId('allContracts');
    if (allContractsId) {
      if (contract?.is_preneed) {
        return [{ label: 'Preneed Contract', submissionId: allContractsId, part: 'all' }];
      }
      return [
        { label: 'Cleo Contract', submissionId: allContractsId, part: 'contract' },
        { label: 'Cremation & Photograph Agreements', submissionId: allContractsId, part: 'agreements' },
        { label: 'Everything (one PDF)', submissionId: allContractsId, part: 'all' },
      ];
    }

    const options: DownloadOption[] = [];
    const cleoId = findId('cleoContract');
    const agreementsId = findId('agreements');
    if (cleoId) options.push({ label: 'Cleo Contract', submissionId: cleoId, part: 'all' });
    if (agreementsId) {
      options.push({ label: 'Cremation & Photograph Agreements', submissionId: agreementsId, part: 'all' });
    }
    return options;
  }, [previewData, contract?.is_preneed]);

  const handleDownload = async (option: DownloadOption) => {
    setDownloadMenuAnchor(null);
    setDownloading(true);
    try {
      const token = await getToken();
      const response = await fetch(
        `${process.env.REACT_APP_PAPERMAN_API_URL}contract/preview/${option.submissionId}/download?part=${option.part}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Refresh-Token': await getRefreshToken(),
          },
        }
      );

      if (!response.ok) {
        let message = 'Failed to download document';
        try {
          const errorData = await response.json();
          message = errorData.message || message;
        } catch {
          // Non-JSON error body — keep the generic message.
        }
        throw new Error(message);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const match = /filename\*=UTF-8''([^;]+)/i.exec(disposition) || /filename="?([^";]+)"?/i.exec(disposition);
      const filename = match ? decodeURIComponent(match[1].trim()) : `${option.label}.pdf`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Revoking synchronously can cancel the download in some browsers.
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error: unknown) {
      console.error('Error downloading contract document:', error);
      notify(error instanceof Error ? error.message : 'Failed to download document', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  };

  // Find the slug for the specified form type
  const getSlugForType = (type: string): string | undefined => {
    if (!previewData?.submissions) return undefined;
    
    const submission = previewData.submissions.find(
      (sub: SubmissionData) => sub.type === type
    );
    return submission?.slug;
  };

  if (!record) return null;

  // Determine the tabs and content to display based on submission types
  const renderPreviewTabs = () => {
    if (!previewData) return null;
    
    // Check if we have a single 'allContracts' submission (same person OR preneed)
    const hasAllContracts = previewData.submissions?.some((sub: SubmissionData) => sub.type === 'allContracts');
    
    if (hasAllContracts) {
      // Single tab for all contracts (same person at-need OR preneed)
      const submission = previewData.submissions.find((sub: SubmissionData) => sub.type === 'allContracts');
      const submissionSlug = submission?.slug;
      
      return (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={0} aria-label="contract preview tabs">
              <Tab label="All Contracts" />
            </Tabs>
          </Box>
          <TabPanel value={0} index={0}>
            {DocusealForm && submissionSlug && (
              <div style={{ height: '100%', width: '100%' }}>
                <DocusealForm
                  src={`${previewData.embedConfig.host}/s/${submissionSlug}`}
                  preview={true}
                />
              </div>
            )}
          </TabPanel>
        </>
      );
    } else {
      // Multiple tabs for different contracts (different people at-need)
      return (
        <>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={currentTab} onChange={handleTabChange} aria-label="contract preview tabs">
              <Tab label="Cleo Contract" />
              <Tab label="Agreements" />
            </Tabs>
          </Box>
          <TabPanel value={currentTab} index={0}>
            {DocusealForm && getSlugForType('cleoContract') && (
              <div style={{ height: '100%', width: '100%' }}>
                <DocusealForm
                  src={`${previewData.embedConfig.host}/s/${getSlugForType('cleoContract')}`}
                  preview={true}
                />
              </div>
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            {DocusealForm && getSlugForType('agreements') && (
              <div style={{ height: '100%', width: '100%' }}>
                <DocusealForm
                  src={`${previewData.embedConfig.host}/s/${getSlugForType('agreements')}`}
                  preview={true}
                />
              </div>
            )}
          </TabPanel>
        </>
      );
    }
  };

  return (
    <>
      <Button
        onClick={handleOpen}
        startIcon={<EmailIcon />}
        size="medium"
        sx={{ mr: 1 }}
      >
        Send Contract
      </Button>
      <Button
        onClick={handlePreviewOpen}
        startIcon={<VisibilityIcon />}
        size="medium"
        color="secondary"
        sx={{ mr: 1 }}
      >
        Preview Contract
      </Button>
      {/* Send Contract Dialog */}
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Send Contract</DialogTitle>
        <DialogContent>
          {(isContractLoading || isFuneralLoading) ? (
            <Loading />
          ) : (
            <Stack spacing={3}>
              <Typography variant="body2" color="text.secondary">
                Select the documents to send and review the recipients below.
              </Typography>

              {/* Documents to Send */}
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  Documents to Send
                </Typography>

                {/* Cleo / Preneed Contract */}
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeContract}
                        onChange={(e) => setIncludeContract(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        {contract?.is_preneed ? 'Preneed Contract' : 'Cleo Contract'}
                      </Typography>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                    {'→ '}
                    {payingClientInfo
                      ? `${payingClientInfo.first_name || ''} ${payingClientInfo.last_name || ''}`.trim()
                      : 'Paying Client'}
                    {payingClientInfo?.email ? ` (${payingClientInfo.email})` : ''}
                  </Typography>
                </Box>

                {/* Crematorium routing suggestion (QC, at-need, no visitation) */}
                {suggestion?.eligible && (
                  <Box
                    sx={{
                      p: 1.5,
                      mb: 1.5,
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'info.light',
                      bgcolor: 'rgba(2, 136, 209, 0.06)',
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} gutterBottom>
                      Crematorium routing
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                      {suggestion.reason}
                    </Typography>
                    {String(suggestion.suggestedPartnerId ?? '') !==
                    String(funeral?.crematorium_partner_id ?? '') ? (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => void applySuggestedPartner()}
                          disabled={applyingSuggestion || !suggestion.suggestedPartnerId}
                        >
                          Use {suggestion.suggestedPartnerName}
                        </Button>
                        <Typography variant="caption" color="text.secondary">
                          currently {crematoriumPartner?.name ?? 'unset'} — change in the case sidebar to override
                        </Typography>
                      </Stack>
                    ) : (
                      <Typography variant="caption" color="success.main">
                        ✓ Crematorium set to {suggestion.suggestedPartnerName}; the{' '}
                        {agreementType === 'cleo' ? 'Cleo' : agreementType} agreement will be sent.
                      </Typography>
                    )}
                  </Box>
                )}
                {suggestionLoading && !suggestion && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    Checking crematorium routing…
                  </Typography>
                )}

                {/* Cremation & Photograph Agreements */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={includeAgreements}
                          onChange={(e) => setIncludeAgreements(e.target.checked)}
                          size="small"
                        />
                      }
                      label={
                        <Typography variant="body2" fontWeight={500}>
                          Cremation & Photograph Agreements
                        </Typography>
                      }
                    />
                    <TextField
                      select
                      size="small"
                      value={agreementType}
                      onChange={(e) => setAgreementType(e.target.value)}
                      disabled={!includeAgreements}
                      sx={{ minWidth: 170 }}
                    >
                      <MenuItem value="rideau">Rideau</MenuItem>
                      <MenuItem value="southwest">Southwest</MenuItem>
                      <MenuItem value="cleo">Cleo Agreements</MenuItem>
                    </TextField>
                  </Box>

                  {isSubsequentContract && !contract?.is_preneed && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                      Off by default for follow-up contracts — re-check to include the cremation &amp; photograph agreements again.
                    </Typography>
                  )}

                  {/* Agreement recipient */}
                  {contract?.is_preneed ? (
                    includeAgreements && (
                      <Box sx={{ pl: 4, mt: 1 }}>
                        <TextField
                          select
                          label="Send to"
                          value={agreementRecipientId}
                          onChange={(e) => setAgreementRecipientId(e.target.value)}
                          size="small"
                          sx={{ minWidth: 280, mb: 1 }}
                        >
                          {(clients || []).map((client: any) => (
                            <MenuItem key={client.id} value={String(client.id)}>
                              {client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim()}
                            </MenuItem>
                          ))}
                          <MenuItem value="other">Someone else</MenuItem>
                        </TextField>
                        {agreementRecipientId === 'other' ? (
                          <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                              label="Name"
                              value={agreementRecipientName}
                              onChange={(e) => setAgreementRecipientName(e.target.value)}
                              size="small"
                              sx={{ minWidth: 200 }}
                            />
                            <TextField
                              label="Email"
                              value={agreementRecipientEmail}
                              onChange={(e) => setAgreementRecipientEmail(e.target.value)}
                              size="small"
                              sx={{ minWidth: 200 }}
                            />
                          </Box>
                        ) : (
                          agreementRecipientName && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              {'→ '}{agreementRecipientName} ({agreementRecipientEmail})
                            </Typography>
                          )
                        )}
                      </Box>
                    )
                  ) : (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                      {'→ '}
                      {isSameRecipient
                        ? `${payingClientInfo?.first_name || ''} ${payingClientInfo?.last_name || ''}`.trim()
                        : `${authorizedClientInfo?.first_name || ''} ${authorizedClientInfo?.last_name || ''}`.trim()
                      }
                      {isSameRecipient
                        ? (payingClientInfo?.email ? ` (${payingClientInfo.email})` : '')
                        : (authorizedClientInfo?.email ? ` (${authorizedClientInfo.email})` : '')
                      }
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* TruStage (T-162): pre-need contracts only, never pre-plan only */}
              {showTrustageBox && (
                <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                    TruStage
                  </Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        id="send-to-trustage-checkbox"
                        checked={sendToTrustage}
                        onChange={(e) => setSendToTrustage(e.target.checked)}
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" fontWeight={500}>
                        Send to TruStage when signed
                      </Typography>
                    }
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: 4 }}>
                    When {payingClientInfo?.first_name || 'the plan holder'} signs, the signed TruStage enrollment form (only
                    that form) is emailed to TruStage, asking them to arrange the payment and to reply with the certificate.
                    The email shows in the case feed; nobody is copied.
                  </Typography>
                  <Box sx={{ pl: 4, mt: 1 }}>
                    <Typography
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() => setTrustagePreviewOpen((v) => !v)}
                      sx={{ background: 'none', border: 0, p: 0, color: 'primary.main', fontWeight: 500, cursor: 'pointer' }}
                    >
                      {trustagePreviewOpen ? '▾' : '▸'} Preview email
                    </Typography>
                    {trustagePreviewOpen && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.100', borderRadius: 1, fontSize: 13, lineHeight: 1.5, overflowWrap: 'anywhere' }}>
                        <div style={{ color: '#666' }}>
                          To: TruStage (set by TRUSTAGE_PRENEED_EMAIL) · Reply-To: the case address · Attachment: TruStage
                          enrollment form (signed)
                        </div>
                        <div style={{ fontWeight: 500, marginTop: 4 }}>
                          New signed Cleo plan · {payingClientInfo ? `${payingClientInfo.first_name} ${payingClientInfo.last_name}` : 'plan holder'} · case {funeral?.id}
                        </div>
                        <div style={{ marginTop: 4 }}>
                          Hello Stéphane, A new Cleo plan was signed. The signed TruStage enrollment form is attached. Plan holder,
                          phone / email, province, plan and price, payment option, contract signed date. Could you please reach out
                          to {payingClientInfo?.first_name || 'the client'} to set up the payment? Once the certificate is issued,
                          please reply to this email with it attached and it will be filed on the case automatically.
                        </div>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* Recipient Details */}
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Recipient Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <ClientInfo
                      clientId={funeral?.paying_client_id}
                      label="Paying Client"
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ClientInfo
                      clientId={funeral?.authorized_client_id}
                      label="Authorized Client"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DeceasedInfo
                      clientId={funeral?.deceased_client_id}
                      funeralData={funeral}
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Warnings */}
              {!funeral?.uuid && (
                <Box sx={{ p: 2, bgcolor: '#ffebee', borderRadius: 1 }}>
                  <Typography color="error">
                    Missing UUID for funeral. Cannot send contract without a valid UUID.
                  </Typography>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            Close
          </Button>
          <Button
            onClick={handleSendContract}
            color="primary"
            variant="contained"
            disabled={loading || !funeral?.uuid || (!includeContract && !includeAgreements)}
          >
            {loading ? 'Sending...' : (
              includeContract && includeAgreements ? 'Send Contract & Agreements' :
              includeContract ? 'Send Contract' :
              includeAgreements ? 'Send Agreements' :
              'Select documents'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Contract Dialog */}
      <Dialog 
        open={previewOpen} 
        onClose={handlePreviewClose}
        maxWidth="lg"
        fullWidth
        sx={{ 
          '& .MuiDialog-paper': { 
            height: '90vh', 
            display: 'flex', 
            flexDirection: 'column' 
          } 
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <span>Preview Contract</span>
          {previewAgreementChoices.length > 1 && previewAgreementType && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Agreement:</Typography>
              <Select
                size="small"
                value={previewAgreementType}
                onChange={(e) => setPreviewAgreementType(e.target.value as string)}
                disabled={previewLoading}
              >
                {previewAgreementChoices.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
              {previewAgreementType !== agreementType && (
                <Chip size="small" color="warning" variant="outlined" label="Not the version that will be sent" />
              )}
            </Box>
          )}
        </DialogTitle>
        <DialogContent sx={{ flexGrow: 1, p: 0, display: 'flex', flexDirection: 'column' }}>
          {(isContractLoading || isFuneralLoading || previewLoading || !previewData) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <Loading />
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
              {renderPreviewTabs()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {downloadOptions.length > 0 && (
            <>
              <Button
                onClick={(e) =>
                  downloadOptions.length === 1
                    ? void handleDownload(downloadOptions[0])
                    : setDownloadMenuAnchor(e.currentTarget)
                }
                startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
                disabled={downloading || previewLoading}
              >
                {downloading ? 'Preparing…' : 'Download'}
              </Button>
              <Menu
                anchorEl={downloadMenuAnchor}
                open={Boolean(downloadMenuAnchor)}
                onClose={() => setDownloadMenuAnchor(null)}
              >
                {downloadOptions.map((option) => (
                  <MenuItem
                    key={`${option.submissionId}-${option.part}`}
                    onClick={() => void handleDownload(option)}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={handlePreviewClose}>Close</Button>
        </DialogActions>
      </Dialog>

    </>
  );
};

export default SendContractButton; 